import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ADDITIONAL_TRAINING_ROLES_LABEL,
  ADDITIONAL_TRAINING_ROLES_TOOLTIP,
} from "../../constants/additionalTrainingRoles";
import "./AdditionalTrainingRolesLabel.scss";

const TOOLTIP_MAX_WIDTH = 280;
const TOOLTIP_ESTIMATED_HEIGHT = 120;
const VIEWPORT_PADDING = 10;
const CLOSE_DELAY_MS = 200;

export default function AdditionalTrainingRolesLabel({
  as: Tag = "label",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [style, setStyle] = useState({
    top: 0,
    left: 0,
    maxWidth: TOOLTIP_MAX_WIDTH,
    transform: "translateY(0)",
  });

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tooltipHeight =
      tooltipRef.current?.offsetHeight || TOOLTIP_ESTIMATED_HEIGHT;
    const tooltipWidth = Math.min(
      TOOLTIP_MAX_WIDTH,
      window.innerWidth - VIEWPORT_PADDING * 2,
    );

    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const spaceBelow =
      window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const showAbove =
      spaceBelow < tooltipHeight + 8 && spaceAbove >= spaceBelow;

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - tooltipWidth - VIEWPORT_PADDING),
    );

    const top = showAbove ? rect.top - 8 : rect.bottom + 8;

    setStyle({
      top,
      left,
      maxWidth: tooltipWidth,
      transform: showAbove ? "translateY(-100%)" : "translateY(0)",
    });
  }, []);

  const showTooltip = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
    requestAnimationFrame(updatePosition);
  }, [clearCloseTimer, updatePosition]);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [pinned, clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (open && tooltipRef.current) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!pinned) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        tooltipRef.current?.contains(target)
      ) {
        return;
      }
      setPinned(false);
      setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [pinned]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  const handleTogglePin = (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearCloseTimer();
    setPinned((current) => {
      const next = !current;
      setOpen(next);
      if (next) {
        requestAnimationFrame(updatePosition);
      }
      return next;
    });
  };

  return (
    <>
      <Tag
        className={["additional-training-roles-label", className]
          .filter(Boolean)
          .join(" ")}
      >
        <span>{ADDITIONAL_TRAINING_ROLES_LABEL}</span>
        <button
          ref={triggerRef}
          type="button"
          className={`additional-training-roles-label__hint-btn${
            open ? " is-active" : ""
          }${pinned ? " is-pinned" : ""}`}
          aria-label={ADDITIONAL_TRAINING_ROLES_TOOLTIP}
          aria-expanded={open}
          onMouseEnter={showTooltip}
          onMouseLeave={scheduleClose}
          onFocus={showTooltip}
          onBlur={scheduleClose}
          onClick={handleTogglePin}
        >
          <i className="fas fa-info-circle" aria-hidden="true" />
        </button>
      </Tag>
      {open
        ? createPortal(
            <div
              ref={tooltipRef}
              className="additional-training-roles-label__popover additional-training-roles-label__popover--portal"
              role="tooltip"
              style={style}
              onMouseEnter={showTooltip}
              onMouseLeave={scheduleClose}
            >
              {ADDITIONAL_TRAINING_ROLES_TOOLTIP}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
